<script lang="ts">
	import { run } from 'svelte/legacy';

	import { Sidebar, SidebarWrapper, SidebarGroup, SidebarItem } from 'flowbite-svelte';
	import Router, { push, location } from '../abstract-spa-router';
	import { isAuthorized, isAdmin } from '../Auth';
	import settingsRoutes from './settings/routes';
	import site from '../site';
	import PageHeader from '../components/PageHeader.svelte';
	import { t } from '../i18n';

	interface Props {
		/** Route params supplied by abstract-spa-router for the Settings wildcard segment. */
		params?: any;
	}

	let { params = $bindable({}) }: Props = $props();
	// NOTE: silence warning
	params = params;

	let activeUrl: string = $state('');
	let activeTopLevelUrl: string = $state('');
	run(() => {
		activeUrl = $location;
		/* Top-level highlight for the first sidebar (Account vs Admin).
		 * Admin sub-pages all highlight the Admin item; Account sub-pages
		 * (My Apps, Sessions) highlight their own specific item. Bare
		 * `/Settings` normalises to `/Settings/Account`. */
		if (activeUrl.startsWith('/Settings/Admin')) {
			activeTopLevelUrl = '/Settings/Admin';
		} else if (activeUrl === '/Settings') {
			activeTopLevelUrl = '/Settings/Account';
		} else {
			activeTopLevelUrl = activeUrl;
		}
	});

	/** Prevent default and push the given settings sub-route via the SPA router. */
	const fnNavigate = (event: Event, href: string) => {
		event.preventDefault();
		push(href);
	};
</script>

<PageHeader title={$t('Settings')} description={$t('Manage and adjust the {{name}} settings.', { name: site.name })} />

<div class="mt-10 lg:flex">
	<Sidebar
		class="flex-none !bg-transparent lg:w-52"
		divClass="!p-0 !bg-transparent !overflow-visible"
		activeUrl={activeTopLevelUrl}
		position="static"
		disableBreakpoints
		backdrop={false}
	>
		{@const activeClass =
			'flex items-center p-1.5 text-sm font-semibold rounded-lg dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 text-primary-500 bg-primary-100 dark:bg-primary-600/20 my-0.5 active'}
		{@const nonActiveClass =
			'flex items-center p-1.5 text-sm font-medium text-gray-700 rounded-lg dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 my-0.5'}
		{@const iconClass =
			'w-5 min-w-5 shrink-0 transition duration-75 group-hover:text-gray-900 dark:group-hover:text-white'}
		<SidebarWrapper class="bg-depth overflow-y-auto rounded-2xl px-3 py-4">
			<SidebarGroup>
				{@const hrefAccount = '/Settings/Account'}
				{@const hrefNotifications = '/Settings/Account/Notifications'}
				{@const hrefMyApps = '/Settings/Account/Applications'}
				{@const hrefSessions = '/Settings/Account/Sessions'}
				{@const hrefPasskeys = '/Settings/Account/Passkeys'}
				{@const hrefAdmin = '/Settings/Admin'}

				<SidebarItem
					{activeClass}
					{nonActiveClass}
					spanClass="ml-3 truncate"
					label={$t('Account')}
					href={hrefAccount}
					onclick={(event) => fnNavigate(event, hrefAccount)}
				>
					{#snippet icon()}
						<i class="fa-solid fa-user {iconClass}"></i>
					{/snippet}
				</SidebarItem>

				<SidebarItem
					{activeClass}
					{nonActiveClass}
					spanClass="ml-3 truncate"
					label={$t('Notifications')}
					href={hrefNotifications}
					onclick={(event) => fnNavigate(event, hrefNotifications)}
				>
					{#snippet icon()}
						<i class="fa-solid fa-bell {iconClass}"></i>
					{/snippet}
				</SidebarItem>

				<SidebarItem
					{activeClass}
					{nonActiveClass}
					spanClass="ml-3 truncate"
					label={$t('My Apps')}
					href={hrefMyApps}
					onclick={(event) => fnNavigate(event, hrefMyApps)}
				>
					{#snippet icon()}
						<i class="fa-solid fa-cubes {iconClass}"></i>
					{/snippet}
				</SidebarItem>

				<SidebarItem
					{activeClass}
					{nonActiveClass}
					spanClass="ml-3 truncate"
					label={$t('Sessions')}
					href={hrefSessions}
					onclick={(event) => fnNavigate(event, hrefSessions)}
				>
					{#snippet icon()}
						<i class="fa-solid fa-desktop {iconClass}"></i>
					{/snippet}
				</SidebarItem>

				<SidebarItem
					{activeClass}
					{nonActiveClass}
					spanClass="ml-3 truncate"
					label={$t('Passkeys')}
					href={hrefPasskeys}
					onclick={(event) => fnNavigate(event, hrefPasskeys)}
				>
					{#snippet icon()}
						<i class="fa-solid fa-fingerprint {iconClass}"></i>
					{/snippet}
				</SidebarItem>

				{#if $isAuthorized && $isAdmin}
					<SidebarItem
						{activeClass}
						{nonActiveClass}
						spanClass="ml-3 truncate"
						label={$t('Admin')}
						href={hrefAdmin}
						onclick={(event) => fnNavigate(event, hrefAdmin)}
					>
						{#snippet icon()}
							<i class="fa-solid fa-gear {iconClass}"></i>
						{/snippet}
					</SidebarItem>
				{/if}
			</SidebarGroup>
		</SidebarWrapper>

		{#if $isAuthorized && $isAdmin && activeUrl.startsWith('/Settings/Admin')}
			<Sidebar
				class="flex-none !bg-transparent lg:w-52"
				divClass="!p-0 !bg-transparent !overflow-visible"
				{activeUrl}
				position="static"
				disableBreakpoints
				backdrop={false}
			>
				{@const activeClass =
					'flex items-center p-1.5 text-sm font-semibold rounded-lg dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 text-primary-500 bg-primary-100 dark:bg-primary-600/20 my-0.5 active'}
				{@const nonActiveClass =
					'flex items-center p-1.5 text-sm font-medium text-gray-700 rounded-lg dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 my-0.5'}
				{@const iconClass =
					'w-5 min-w-5 shrink-0 transition duration-75 group-hover:text-gray-900 dark:group-hover:text-white'}
				<SidebarWrapper class="bg-depth mt-4 overflow-y-auto rounded-2xl px-3 py-4">
					<SidebarGroup>
						{@const hrefAdmin = '/Settings/Admin'}
						{@const hrefGeneral = '/Settings/Admin/General'}
						{@const hrefSMTP = '/Settings/Admin/SMTP'}
						{@const hrefSecurity = '/Settings/Admin/Security'}
						{@const hrefLogs = '/Settings/Admin/Logs'}
						{@const hrefEmailTemplates = '/Settings/Admin/EmailTemplates'}
						{@const hrefLicensing = '/Settings/Admin/Licensing'}
						{@const hrefSupport = '/Settings/Admin/Support'}
						{@const hrefUserDashboard = '/Settings/Admin/UserDashboard'}
						{@const hrefAnalyticsDashboard = '/Settings/Admin/Dashboard'}
						{@const hrefMetrics = '/Settings/Admin/Metrics'}
						{@const hrefTokenExport = '/Settings/Admin/TokenExport'}

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Overview')}
							href={hrefAdmin}
							onclick={(event) => fnNavigate(event, hrefAdmin)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-gear {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Dashboard')}
							href={hrefAnalyticsDashboard}
							onclick={(event) => fnNavigate(event, hrefAnalyticsDashboard)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-chart-line {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Metrics')}
							href={hrefMetrics}
							onclick={(event) => fnNavigate(event, hrefMetrics)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-chart-simple {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Token Export')}
							href={hrefTokenExport}
							onclick={(event) => fnNavigate(event, hrefTokenExport)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-file-export {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('General')}
							href={hrefGeneral}
							onclick={(event) => fnNavigate(event, hrefGeneral)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-sliders {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('SMTP')}
							href={hrefSMTP}
							onclick={(event) => fnNavigate(event, hrefSMTP)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-envelope {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Security')}
							href={hrefSecurity}
							onclick={(event) => fnNavigate(event, hrefSecurity)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-shield-halved {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Email Templates')}
							href={hrefEmailTemplates}
							onclick={(event) => fnNavigate(event, hrefEmailTemplates)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-envelope-open-text {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Licensing')}
							href={hrefLicensing}
							onclick={(event) => fnNavigate(event, hrefLicensing)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-key {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Support')}
							href={hrefSupport}
							onclick={(event) => fnNavigate(event, hrefSupport)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-life-ring {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('User Dashboard')}
							href={hrefUserDashboard}
							onclick={(event) => fnNavigate(event, hrefUserDashboard)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-gauge-high {iconClass}"></i>
							{/snippet}
						</SidebarItem>

						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass="ml-3 truncate"
							label={$t('Audit Logs')}
							href={hrefLogs}
							onclick={(event) => fnNavigate(event, hrefLogs)}
						>
							{#snippet icon()}
								<i class="fa-solid fa-list {iconClass}"></i>
							{/snippet}
						</SidebarItem>
					</SidebarGroup>
				</SidebarWrapper>
			</Sidebar>
		{/if}
	</Sidebar>
	<div class="mt-10 w-full lg:mt-0 lg:ml-10">
		<Router routes={settingsRoutes} />
	</div>
</div>
